<script setup lang="ts">
const { locale: currentLocale, locales, defaultLocale } = useI18n()
const switchLocalePath = useSwitchLocalePath()

// the default locale leads the switcher, whatever order the config lists them in
const sortedLocales = computed(() =>
  [...locales.value].sort((a, b) =>
    Number(b.code === defaultLocale) - Number(a.code === defaultLocale)
  )
)

// 1
const menuData = useState('menuData')
const sharedData = useState('sharedData')

// 2
const showMenu = useState('showMenu')

useEscapeLayer(showMenu, () => {
  showMenu.value = false
})

// a modal closing itself because the menu opened on top of it must not close
// the menu along with it
const keepMenuOnNavigation = useState('keepMenuOnNavigation', () => false)

const nuxtApp = useNuxtApp()
nuxtApp.hook('page:finish', () => {
  if (keepMenuOnNavigation.value) {
    keepMenuOnNavigation.value = false
    return
  }
  showMenu.value = false
})

const contentTarget = ref<HTMLElement | null>(null)
onClickOutside(contentTarget, (e) => {
  if (showMenu.value) {
    showMenu.value = false
  }
})

// Switching locale re-renders the page underneath the menu, so close the menu
// first and only navigate once its leave transition has finished.
const pendingLocalePath = ref<string | null>(null)

function onLocaleClick(code: Parameters<typeof switchLocalePath>[0]) {
  const path = switchLocalePath(code)
  if (!path) return

  if (!showMenu.value) {
    navigateTo(path)
    return
  }

  pendingLocalePath.value = path
  showMenu.value = false
}

function onAfterLeave() {
  const path = pendingLocalePath.value
  if (!path) return

  pendingLocalePath.value = null
  navigateTo(path)
}

// re-opening cancels the leave transition, so drop the queued switch
watch(showMenu, (open) => {
  if (open) pendingLocalePath.value = null
})
</script>

<template>
  <transition
    name="overlay-fade"
    @after-leave="onAfterLeave"
  >
    <FocusScope
      v-if="showMenu"
      as="menu"
      class="p-1 flex flex-col gap-2 bg-white shadow-[0_0_1em_rgba(0,0,0,0.05)] fixed right-0 top-0 max-h-screen z-300 overflow-scroll"
      loop
      trapped
    >
      <div
        class="flex gap-1 text-md"
        ref="contentTarget"
      >
        <div>
          <template
            v-for="(locale, index) in sortedLocales"
            :key="locale.code"
          >
            <NuxtLink
              :to="switchLocalePath(locale.code)"
              :data-current="locale.code === currentLocale ? '' : null"
              class="uppercase data-[current]:text-accent hover:text-accent focus-visible:text-accent"
              @click.exact.prevent="onLocaleClick(locale.code)"
            >
              {{ locale.code }}
            </NuxtLink>{{ index < sortedLocales.length - 1 ? ' / ' : '' }}
          </template>
        </div>

        <button
          @click="showMenu = false"
          class="group ml-auto icon-box-cross overflow-visible relative"
        >
          <svgo-cross class="absolute inset-0 stroke-default stroke-current w-full overflow-visible group-hover:stroke-accent group-focus-visible:stroke-accent icon-hover" />
        </button>
      </div>

      <div class="flex gap-1 flex-1">
        <ul
          class="flex flex-col gap-1"
        >
          <li
            v-for="group in menuData?.groups"
            :key="group.id"
            class="text-md"
          >
            <ul>
              <li
                v-for="link in group.links"
                :key="link.id"
              >
                <site-payload-link
                  :data="link.link"
                  break-on-ampersand
                  :style="{
                    '--accent--color': link?.color
                  }"
                  class="hover:text-[var(--accent--color,var(--color--gray))] focus-visible:text-[var(--accent--color,var(--color--gray))] data-[current]:text-[var(--accent--color,var(--color--gray))] data-[is-parent]:text-[var(--accent--color,var(--color--gray))]"
                />
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </FocusScope>
  </transition>
</template>
